import { autoinject } from "aurelia-framework";

import { default as notificationHelper } from "helpers/notificationHelper";

import { ProcoreConfigurationService } from "services/procore/procore-configuration-service";
import { ProcoreCompanyConfigurationModel } from "api/models/company/procore/procore-company-configuration-model";

import { ConfigurationAuthentification } from "./composition/authentification";
import { ConfigurationEntities } from "./composition/entities";
import { I18N } from "aurelia-i18n";
import { ProcoreEntityConfigurationProjectFilters } from "enums/project-filters";
import { SideBar } from "./composition/side-bar";
import { ConfigurationActivities } from "./composition/activities";
import { ConfigurationOrdersSynchronization } from "./composition/orders";
import { CanCloseWindow } from "interfaces/can-close-window";
import HostContext from "core/host-context";
import { ConfigurationJobSystem } from "./composition/jobsystem";

@autoinject()
export class ProcoreConfiguration implements CanCloseWindow {
  public isDirty: boolean = false;
  private self: ProcoreConfiguration = this;

  constructor(private readonly i18n: I18N,

              private readonly authentification: ConfigurationAuthentification,
              private readonly entities: ConfigurationEntities,
              private readonly sidebar: SideBar,
              private readonly activities: ConfigurationActivities,
              private readonly jobsystem: ConfigurationJobSystem,
              private readonly orders: ConfigurationOrdersSynchronization,

              private readonly procoreConfigurationService: ProcoreConfigurationService,

              private readonly hostContext: HostContext) {

    this.sidebar.configurations = this;
    this.authentification.configurations = this;
    this.activities.configurations = this;

    this.sidebar.Items.push({ code: "authentification", label: this.i18n.tr("Procore.Authentication"), isSelected: true});
    this.sidebar.Items.push({ code: "entities", label: this.i18n.tr("Procore.Config_display_settings") });
    this.sidebar.Items.push({ code: "projects", label: this.i18n.tr("Projects") });
    this.sidebar.Items.push({ code: "orders", label: this.i18n.tr("Orders") });
    this.sidebar.Items.push({ code: "jobsystem", label: this.i18n.tr("Procore.JobSystem") });
  }

  public setDirty(value: boolean): void {
    this.isDirty = value;
  }

  public async canClose(): Promise<void> {
    if (this.isDirty) {
      this.ifIsDirtyShowPopup(true);
    } else {
      (this.hostContext as any).viewModel.closeWindow();
    }
  }

  public async attached(): Promise<void> {
    (window as any).CurrentViewModel = this;

    await this.Load();
  }

  public async Load(): Promise<void> {
    await this.LoadLogic();

    this.setDirty(false);
  }

  public async save(): Promise<boolean> {
    try {
      const errorMessage = this.entities.validate();
      if (errorMessage) {
          notificationHelper.showError(errorMessage, "");
          return false;
      }

      const saveModel = {
        // Authentification
        ProcoreApiUrl: this.authentification.isCustomUrl ? this.authentification.urlCustom
                                                         : this.authentification.urlSelected.text,

        ProcoreCompanyId: this.authentification.companyId,
        ProcoreApiClientId: this.authentification.clientId,
        ProcoreApiClientSecret: this.authentification.clientSecret,
        ProcoreCompanyName: this.authentification.procoreCompanyName,

        // Entities
        EntitiesToSynchronize: this.entities.entities,

        ConcatenationModeEnabled: this.activities.concatModeEnabled,
        ConcatenationSeparator: this.activities.defaultSeparator.id,

        // Orders
        OrderSynchronizationOrderType: this.orders.SelectedOrderType

      } as ProcoreCompanyConfigurationModel;

      const procoreCompanyName = await this.procoreConfigurationService.SaveProcoreConfiguration(saveModel);

      this.authentification.procoreCompanyName = procoreCompanyName;

      notificationHelper.showSuccess(this.i18n.tr(`Procore.Status_Success`), "");

      this.setDirty(false);
      return true;
    } catch {
      notificationHelper.showError(this.i18n.tr(`Procore.Config_Save_Error`), "");
      return false;
    }
  }

  public async resetFields(): Promise<void> {
    if (this.isDirty) {
      await notificationHelper.showDialogResetFields(this.i18n.tr("Procore.ConfigurationsResetFields"))
      .then(async (success: any) => {
        if (success) {
          await this.Load();
        }
      });
    }
  }

  public async ifIsDirtyShowPopup(closeWindow: boolean = false): Promise<void> {
      if (this.isDirty) {
        let isSaveSuccessful = true;
        await notificationHelper.showDialogSaveCancel(this.i18n.tr("Procore.ConfigurationsDirty"))
        .then(async (success: any) => {
          if (success) {
            isSaveSuccessful = await this.save();
          }
        })
        .then(() => {
          if (!isSaveSuccessful) {
              return;
          }
          if (closeWindow) {
            (this.hostContext as any).viewModel.closeWindow();
          } else {
            this.Load();
          }
        });
      }
    }

  private async LoadLogic(): Promise<void> {
    try {
      const data = await this.procoreConfigurationService.GetProcoreConfiguration();
      const jobs = await this.procoreConfigurationService.GetAllJobStatuses();

      if (!data) {
        throw new Error();
      }

      this.authentification.Load(data);
      this.entities.Load(data);
      this.activities.Load(data);
      this.orders.Load(data);
      this.jobsystem.Load(jobs);

    } catch {
      notificationHelper.showError(this.i18n.tr(`Procore.Config_Load_Error`), "");
    }
  }
}
